import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PRODUCTS } from "../lib/site";
import Reveal from "../components/Reveal";

const Services = () => {
    return (
        <div data-testid="services-page">
            {/* Hero */}
            <section className="pt-12 pb-16 lg:pt-20 lg:pb-20">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="text-xs uppercase tracking-widest2 text-copper mb-6">— Catalogue</div>
                        <h1 className="font-serif text-5xl lg:text-7xl font-light text-forest leading-[0.95] tracking-tight max-w-4xl text-balance">
                            The materials we make, <span className="italic text-copper">and the weaves they become.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={150}>
                        <p className="mt-8 max-w-2xl text-lg text-charcoal/75 leading-relaxed">
                            Three product families, manufactured to spec, supplied at scale. Bespoke colourways, custom
                            profiles, and exclusive runs available for established furniture brands.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Product blocks */}
            <section className="border-t border-line">
                {PRODUCTS.map((p, i) => (
                    <div
                        key={p.id}
                        data-testid={`service-block-${p.id}`}
                        className={`border-b border-line ${i % 2 === 1 ? "bg-surface-alt" : "bg-bone"}`}
                    >
                        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                            <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                                <Reveal>
                                    <div className="aspect-[4/3] lg:aspect-[5/6] overflow-hidden border border-line">
                                        <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                                    </div>
                                </Reveal>
                            </div>
                            <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                                <Reveal delay={120}>
                                    <div className="text-xs uppercase tracking-widest2 text-copper mb-4">— 0{i + 1} · {p.kicker}</div>
                                    <h2 className="font-serif text-4xl lg:text-5xl text-forest leading-tight">{p.name}</h2>
                                    <p className="mt-6 text-lg text-charcoal/75 max-w-xl leading-relaxed">{p.summary}</p>
                                    <ul className="mt-8 space-y-4">
                                        {p.bullets.map((b, j) => (
                                            <li key={j} className="flex items-start gap-3 text-charcoal/85">
                                                <Check size={18} className="mt-1 text-copper" strokeWidth={2} />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-10 flex flex-wrap gap-4">
                                        <Link
                                            to="/contact"
                                            data-testid={`service-${p.id}-cta`}
                                            className="inline-flex items-center gap-3 px-6 py-3.5 bg-forest text-bone hover:bg-charcoal transition-colors"
                                        >
                                            Request swatches <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </Reveal>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            {/* Process */}
            <section className="py-24 lg:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="text-xs uppercase tracking-widest2 text-copper mb-6">— How we work</div>
                        <h2 className="font-serif text-4xl lg:text-5xl text-forest max-w-2xl leading-tight">
                            From inquiry to your loading dock.
                        </h2>
                    </Reveal>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
                        {[
                            { n: "01", t: "Inquiry", d: "Share your application, target colours, and quantities." },
                            { n: "02", t: "Swatch", d: "We courier physical samples for hand & colour approval." },
                            { n: "03", t: "Production", d: "Approved profiles enter our extrusion and finishing lines." },
                            { n: "04", t: "Dispatch", d: "Sea or air freight with full export documentation." },
                        ].map((s, i) => (
                            <Reveal key={i} delay={i * 100}>
                                <div className="border border-line p-8 h-full bg-bone hover:border-forest transition-colors duration-500">
                                    <div className="font-serif text-5xl text-copper">{s.n}</div>
                                    <div className="font-serif text-2xl text-forest mt-4">{s.t}</div>
                                    <p className="mt-3 text-sm text-charcoal/70 leading-relaxed">{s.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Services;
